/**
 * LoadProgress use case (contracts.md): load save, restore state, render map.
 */

import { createSession, wallCost, TOWER_BASE_COST, TOWER_COST_INCREMENT, INVENTORY_MAX, type GameSession } from "../../domain/session.js";
import { deserializeSaveData, deserializeMapProgress, applySessionSnapshot, type CurrentSessionSnapshot, type SerializedGem } from "../../domain/save.js";
import { createInitialMapProgress } from "../../domain/map-progress.js";
import type { MapProgress } from "../../domain/map-progress.js";
import { type MageProfile, createMageProfile } from "../../domain/mage-profile.js";
import type { BuildingType, FieldConfig } from "../../domain/types.js";
import type { Gem } from "../../domain/entities.js";
import type { LoadResult, AppState } from "../types.js";
import type { RenderPort, StoragePort, FieldConfigPort } from "../ports.js";
import { mapStateToView } from "../state-view.js";

export interface LoadProgressDeps {
  storage: StoragePort;
  render: RenderPort;
  fieldConfig: FieldConfigPort;
  startingFieldId: string;
  setSession(session: GameSession | null): void;
  setMapProgress(mp: MapProgress): void;
  setMageProfile?: (profile: MageProfile) => void;
}

function hydrateGem(s: SerializedGem, id: string): Gem {
  const g: Gem = { id, type: s.type, level: s.level };
  if (s.secondaryType !== undefined) g.secondaryType = s.secondaryType;
  if (s.mods !== undefined) g.mods = { ...s.mods };
  return g;
}

function restoreSessionFromSnapshot(
  fieldId: string,
  config: FieldConfig,
  snapshot: CurrentSessionSnapshot
): GameSession {
  const session = createSession(fieldId, config);
  applySessionSnapshot(session, snapshot);
  session.currentWaveIndex = snapshot.waveIndex;
  session.mana.current = snapshot.mana;
  if (snapshot.maxMana !== undefined) session.mana.max = snapshot.maxMana;
  if (snapshot.towersBuilt !== undefined) session.towersBuilt = snapshot.towersBuilt;
  let wallsBuiltCount = 0;
  let towersBuiltCount = 0;
  session.towers = snapshot.towers.map((t, i) => {
    const isWall = t.type === "Wall";
    // Exact originalCost isn't recoverable from old saves; approximation accepted.
    const originalCost = isWall
      ? wallCost(wallsBuiltCount++)
      : (TOWER_BASE_COST + TOWER_COST_INCREMENT * towersBuiltCount++);
    return {
      id: `tower-${i}`,
      cell: { ...t.cell },
      type: t.type as BuildingType,
      gemSlots: t.gems.map((g, j) => ({
        gem: g ? hydrateGem(g, `gem-${i}-${j}`) : null,
      })),
      fireCooldownMs: 0,
      fireRateMs: isWall ? 0 : 1000,
      originalCost,
    };
  });
  session.wallsBuilt = wallsBuiltCount;
  // B16: truncate to cap so old saves with >4 gems don't overflow rendered slots
  session.gemInventory = (snapshot.gemInventory ?? []).slice(0, INVENTORY_MAX).map((g, k) => hydrateGem(g, `inv-${k}`));
  session.speed = 0;
  return session;
}

export function createLoadProgress(deps: LoadProgressDeps) {
  return async function loadProgress(): Promise<LoadResult> {
    const raw = await deps.storage.load();
    const data = raw !== null ? deserializeSaveData(raw) : null;
    const mp: MapProgress = data !== null && data.mapProgress
      ? deserializeMapProgress(data.mapProgress)
      : createInitialMapProgress(deps.startingFieldId);
    deps.setMapProgress(mp);

    const mageProfile: MageProfile =
      data !== null && data.mageProfile != null
        ? data.mageProfile
        : createMageProfile();
    deps.setMageProfile?.(mageProfile);

    if (data === null) {
      const state: AppState = { scene: "map", mapProgress: mp };
      deps.render.setScene("map");
      deps.render.update(mapStateToView(state, mageProfile));
      return { ok: true, state };
    }
    // Landing page is always the map. An in-progress session is kept in
    // memory so the player can resume by clicking the same field, but we
    // never auto-enter battle on load.
    const cs = data.currentSession;
    if (cs) {
      const config = deps.fieldConfig.getFieldConfig(cs.fieldId);
      if (config === null) {
        return { ok: false, code: "CORRUPT_SAVE" };
      }
      const session = restoreSessionFromSnapshot(cs.fieldId, config, cs);
      deps.setSession(session);
    }
    const state: AppState = {
      scene: "map",
      mapProgress: mp,
    };
    deps.render.setScene("map");
    deps.render.update(mapStateToView(state, mageProfile));
    return { ok: true, state };
  };
}
